import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ErrorCard from '../ErrorCard/ErrorCard';
import ConversationsResponse from '../../../types/Analytics/ConversationsResponse';
import generateColours from '../generateColours';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ChatbotUsageDoughnutChartProps {
  conversationsData?: ConversationsResponse;
  isLoading?: boolean;
  isSmallScreen?: boolean;
}

function ChatbotUsageDoughnutChart({
  conversationsData,
  isLoading,
  isSmallScreen
}: ChatbotUsageDoughnutChartProps): React.ReactElement | null {
  const [chartData, setChartData] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (conversationsData) {
      const colours = generateColours(3);
      const formattedChartData = {
        labels: ['01:00 - 09:00', '09:00 - 17:00', '17:00 - 01:00'],
        datasets: [
          {
            label: 'Messages by time of day',
            data: [
              conversationsData.messages0109,
              conversationsData.messages0917,
              conversationsData.messages1701
            ],
            backgroundColor: colours,
            hoverOffset: 4
          }
        ]
      };
      setChartData(formattedChartData);
    } else {
      setChartData(null);
    }
  }, [conversationsData]);

  if (errorMessage) {
    return <ErrorCard errorMessage={errorMessage} />;
  }

  if (
    chartData &&
    chartData.datasets[0].data.reduce(
      (acc: number, val: number) => acc + val,
      0
    ) > 0
  ) {
    return (
      <div
        role='img'
        aria-label='Chatbot usage chart'
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isSmallScreen ? 180 : 220
        }}
      >
        <Doughnut
          data={chartData}
          options={{
            cutout: '70%',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                  boxWidth: 12,
                  padding: 10,
                  font: {
                    size: 12
                  }
                }
              },
              tooltip: {
                callbacks: {
                  label: (tooltipItem) => {
                    return ` ${tooltipItem.raw} messages`;
                  }
                }
              }
            },
            layout: {
              padding: 8
            }
          }}
        />
      </div>
    );
  }

  return null;
}

export default ChatbotUsageDoughnutChart;
